import { classify } from 'sidecar-utilities'

import { ButtonSentiment, ButtonStyleProps, ButtonSize } from './Button.types'

const getIconOnlyStyles = (size: ButtonSize) => {
  const sizeMap = {
    [ButtonSize.LARGE]: 'h-12 w-12',
    [ButtonSize.MEDIUM]: 'h-10 w-10',
    [ButtonSize.SMALL]: 'h-8 w-8',
  }

  return `rounded-full min-content ${sizeMap[size] || sizeMap[ButtonSize.MEDIUM]}`
}

const BUTTON_SENTIMENT_STYLES = {
  [ButtonSentiment.PRIMARY]: {
    activeState: `active:color-bgPrimary-active active:color-textOnPrimary`,
    defaultState: `color-bgPrimary color-textOnPrimary`,
    disabledState: `color-bgPrimary-disabled color-textOnPrimary`,
    focus: `focus:color-textOnPrimary focus:color-outlinePrimaryDecor
        focus:outline outline-4`,
    hoverState: `hover:color-bgPrimary-hover hover:color-textOnPrimary`,
    iconSentiment: `[&_svg]:color-iconOnPrimary`,
    border: '',
  },
  [ButtonSentiment.PRIMARY_ON_COLOR]: {
    activeState: `active:color-bgDefaultContainer-active 
      active:color-textPrimary-active`,
    defaultState: `color-bgDefaultContainer
        color-textPrimary`,
    disabledState: `color-bgDefaultContainer
        color-textPrimary-disabled`,
    focus: `focus:color-outlinePrimaryDecor
        focus:outline outline-4`,
    hoverState: `hover:color-bgDefaultContainer-hover hover:color-textPrimary-hover`,
    iconSentiment: `[&_svg]:disabled:color-iconPrimary-disabled
        [&_svg]:color-iconPrimary
        [&_svg]:hover:color-iconPrimary-hover
        [&_svg]:hover:disabled:color-iconPrimary-disabled
        [&_svg]:active:color-iconPrimary-active
        [&_svg]:active:disabled:color-iconPrimary-disabled`,
    border: '',
  },
  [ButtonSentiment.SECONDARY]: {
    activeState: `active:color-bgDefault-active`,
    defaultState: `bg-transparent color-borderPrimary
        color-textPrimary`,
    disabledState: `color-borderPrimary-disabled
        color-textPrimary-disabled`,
    focus: `focus:color-outlinePrimaryDecor
        focus:outline outline-4`,
    hoverState: `hover:color-bgDefault-hover hover:color-textPrimary-hover`,
    iconSentiment: `[&_svg]:disabled:color-iconPrimary-disabled
        [&_svg]:color-iconPrimary
        [&_svg]:hover:color-iconPrimary-hover
        [&_svg]:hover:disabled:color-iconPrimary-disabled
        [&_svg]:active:color-iconPrimary-active
        [&_svg]:active:disabled:color-iconPrimary-disabled`,
    border: `border`,
  },
  [ButtonSentiment.SECONDARY_ON_COLOR]: {
    activeState: `active:color-bgPrimary-active active:color-textOnPrimary`,
    defaultState: `color-borderOnPrimary color-textOnPrimary`,
    disabledState: `color-borderOnPrimary-disabled`,
    focus: `focus:color-textOnPrimary focus:color-outlinePrimaryDecor
        focus:outline outline-4`,
    hoverState: `hover:color-bgPrimary-hover hover:color-textOnPrimary`,
    iconSentiment: `[&_svg]:color-iconOnPrimary`,
    border: `border`,
  },
  [ButtonSentiment.ACCENT]: {
    activeState: `active:color-accent-weak-active active:color-textOnAccent-default`,
    defaultState: `color-accent-weak-default 
        color-textOnAccent-default`,
    disabledState: `color-accent-weak-disabled 
        color-textOnAccent-disabled`,
    focus: `focus:color-outlineAccent-default 
        focus:outline outline-4`,
    hoverState: `hover:color-accent-weak-hover hover:color-textOnAccent-default`,
    iconSentiment: `[&_svg]:disabled:color-iconOnAccent-disabled
        [&_svg]:color-iconOnAccent-default`,
    border: '',
  },
  [ButtonSentiment.NOTIFY]: {
    activeState: `active:color-notify-weak-active active:color-textOnNotify-default`,
    defaultState: `color-notify-weak-default 
        color-textOnNotify-default`,
    disabledState: `color-notify-weak-disabled 
        color-textOnNotify-disabled`,
    focus: `focus:color-outlineNotify-default 
        focus:outline outline-4`,
    hoverState: `hover:color-notify-weak-hover hover:color-textOnNotify-default`,
    iconSentiment: `[&_svg]:disabled:color-iconOnNotify-disabled
        [&_svg]:color-iconOnNotify-default`,
    border: '',
  },
  [ButtonSentiment.RISK]: {
    activeState: `active:color-risk-weak-active active:color-textOnRisk-default`,
    defaultState: `color-risk-weak-default 
        color-textOnRisk-default`,
    disabledState: `color-risk-weak-disabled 
        color-textOnRisk-disabled`,
    focus: `focus:color-outlineRisk-default 
        focus:outline outline-4`,
    hoverState: `hover:color-risk-weak-hover hover:color-textOnRisk-default`,
    iconSentiment: `[&_svg]:disabled:color-iconOnRisk-disabled
        [&_svg]:color-iconOnRisk-default`,
    border: '',
  },
  [ButtonSentiment.HIGH_RISK]: {
    activeState: `active:color-highRisk-active active:color-textOnHighRisk-default`,
    defaultState: `color-bgRisk 
        color-textOnRisk`,
    disabledState: `color-highRisk-disabled 
        color-textOnHighRisk-disabled`,
    focus: `focus:color-outlineRisk-default 
        focus:outline outline-4`,
    hoverState: `hover:color-highRisk-hover hover:color-textOnHighRisk-default`,
    iconSentiment: `[&_svg]:disabled:color-iconOnHighRisk-disabled
        [&_svg]:color-iconOnHighRisk-default`,
    border: '',
  },
  [ButtonSentiment.LINK]: {
    activeState: 'active:color-textPrimary-active',
    defaultState: 'color-textPrimary',
    disabledState: 'color-textPrimary-disabled',
    focus:
      'focus:color-textPrimary-hover color-outlinePrimaryDecor ring-0 ring-offset-0',
    hoverState: 'hover:color-textPrimary-hover hover:underline focus:underline',
    iconSentiment:
      '[&_svg]:disabled:color-iconPrimary-disabled [&_svg]:color-iconPrimary',
    border: '',
  },
  [ButtonSentiment.LINK_ON_COLOR]: {
    activeState: 'active:color-textOnPrimary',
    defaultState: 'color-textOnPrimary',
    disabledState: 'color-textOnPrimary-disabled',
    focus:
      'focus:color-textOnPrimary color-outlinePrimaryDecor ring-0 ring-offset-0',
    hoverState: 'hover:color-textOnPrimary hover:underline focus:underline',
    iconSentiment:
      '[&_svg]:disabled:color-iconOnPrimary-disabled [&_svg]:color-iconOnPrimary',
    border: '',
  },
  [ButtonSentiment.SIDEKICK]: {
    activeState: `active:color-sidekick-active active:color-textOnSidekick-default`,
    defaultState: `color-sidekick-default
        color-textOnSidekick-default`,
    disabledState: `color-sidekick-disabled
        color-textOnSidekick-disabled`,
    focus: `focus:color-textOnSidekick-default focus:color-outlineSidekick-heavy-default
        focus:outline outline-4`,
    hoverState: `hover:color-sidekick-hover hover:color-textOnSidekick-default`,
    iconSentiment: `[&_svg]:disabled:color-iconOnSidekick-disabled
        [&_svg]:color-iconOnSidekick-default`,
    border: '',
  },
  [ButtonSentiment.STARTER]: {
    activeState: `active:color-sidekick-weak-active active:color-textOnSidekick-default`,
    defaultState: `color-sidekick-weak-default
        color-textOnSidekick-default`,
    disabledState: `color-sidekick-weak-disabled
        color-textOnSidekick-disabled`,
    focus: `focus:color-textOnSidekick-default focus:color-outlineSidekick-bold-default
        focus:outline outline-4`,
    hoverState: `hover:color-sidekick-weak-hover hover:color-textOnSidekick-default`,
    iconSentiment: `[&_svg]:disabled:color-iconOnSidekick-disabled
        [&_svg]:color-iconOnSidekick-default`,
    border: '',
  },
} as const

export const buttonStyles = ({
  busy,
  children,
  disabled,
  icon,
  sentiment = ButtonSentiment.PRIMARY,
  size = ButtonSize.MEDIUM,
  width,
}: ButtonStyleProps) => {
  const {
    activeState,
    border,
    defaultState,
    disabledState,
    focus,
    hoverState,
    iconSentiment,
  } =
    BUTTON_SENTIMENT_STYLES[sentiment] ??
    BUTTON_SENTIMENT_STYLES[ButtonSentiment.PRIMARY]

  const looksLikeLink =
    sentiment === ButtonSentiment.LINK ||
    sentiment === ButtonSentiment.LINK_ON_COLOR

  const hasBorderRadius = children && !looksLikeLink

  return classify({
    activeState: !disabled && !busy ? activeState : null,
    border,
    disabledState: disabled || busy ? disabledState : defaultState,
    focus: !disabled && !busy ? focus : null,
    hoverState: !disabled && !busy ? hoverState : null,
    iconSentiment,
    iconOnlyStyles: !children && icon ? getIconOnlyStyles(size) : null,
    layout: 'inline-flex items-center justify-center',
    radius: hasBorderRadius ? 'rounded-3xl' : null,
    internalSpacing: children ? 'gap-1' : null,
    externalSpacing: !looksLikeLink ? 'mb-4 last:mb-0' : null,
    typographyLarge: size === 'large' && children ? 'sh-body' : '',
    typographyMedium: size === 'medium' && children ? 'sh-body-sm' : '',
    typographySmall: size === 'small' && children ? 'sh-body-xs' : '',
    heightLarge:
      size === 'large' && children && !looksLikeLink ? 'h-12 px-6' : '',
    heightMedium:
      size === 'medium' && children && !looksLikeLink ? 'h-10 px-5' : '',
    heightSmall:
      size === 'small' && children && !looksLikeLink ? 'h-8 px-3' : '',
    width: children ? (width === 'full' ? 'w-full' : 'w-min') : null,
    transition: 'ease-in duration-150',
    alignment: looksLikeLink ? 'text-left' : null,
  })
}
